import React from 'react';
import { motion } from 'framer-motion';
import perfil from '../../assets/image/portafolio/perfil.jpg';
import facebook from '../../assets/image/social/facebook.png';
import instagram from '../../assets/image/social/instagram.png';
import tiktok from '../../assets/image/social/tiktok-white.svg';

const Banner = () => {
    return (
        <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-12">
            {/* Background Effects */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
                <div className="absolute -top-40 -left-40 w-96 h-96 bg-accent-purple/20 rounded-full blur-[100px]"></div>
                <div className="absolute top-40 -right-40 w-96 h-96 bg-accent-cyan/20 rounded-full blur-[100px]"></div>
                <div className="absolute -bottom-40 left-1/2 w-[30rem] h-[30rem] bg-accent-blue/20 rounded-full blur-[120px] transform -translate-x-1/2"></div>
            </div>

            <div className="container mx-auto px-4 z-10 relative">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                    {/* Text Content */}
                    <motion.div 
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="flex-1 text-center lg:text-left"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2, duration: 0.5 }}
                            className="inline-block mb-6 px-5 py-2 rounded-full border border-accent-cyan/30 bg-accent-cyan/10 text-accent-cyan font-medium text-sm tracking-wide shadow-[0_0_15px_rgba(34,211,238,0.2)]"
                        >
                            🚀 Full Stack Web Developer
                        </motion.div>
                        
                        <h1 className="text-5xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight">
                            Hola, soy <br/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-blue via-accent-cyan to-accent-purple inline-block pb-2">
                                Cesar Fuentes
                            </span>
                        </h1>
                        
                        <p className="text-lg lg:text-xl text-slate-400 mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-light">
                            <strong className="text-slate-200 font-medium">Más de 8 años construyendo experiencias digitales.</strong><br/>
                            Apasíonado por crear soluciones modernas, eficientes y bien estructuradas. Transformando ideas complejas en productos digitales de alta calidad.
                        </p>

                        <div className="flex justify-center lg:justify-start gap-5 mb-12">
                            <motion.a 
                                whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(168, 85, 247, 0.4)" }}
                                whileTap={{ scale: 0.95 }}
                                href="#portfolio" 
                                className="btn-primary inline-flex items-center justify-center"
                            >
                                Ver Proyectos
                            </motion.a>
                            <motion.a 
                                whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.05)" }}
                                whileTap={{ scale: 0.95 }}
                                href="#contact" 
                                className="btn-outline-light inline-flex items-center justify-center"
                            >
                                Contáctame
                            </motion.a>
                        </div>

                        {/* Social Links */}
                        <div className="flex justify-center lg:justify-start gap-4">
                            {[
                                { url: "https://www.facebook.com/cesarfuentesdev2", icon: facebook, alt: "Facebook" },
                                { url: "https://www.instagram.com/cesarfuentesdev1/", icon: instagram, alt: "Instagram" },
                                { url: "https://www.tiktok.com/@cesarfuentesdev", icon: tiktok, alt: "TikTok" }
                            ].map((social, index) => (
                                <motion.a
                                    key={index}
                                    href={social.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    whileHover={{ y: -5, scale: 1.1, borderColor: "rgba(34, 211, 238, 0.5)" }}
                                    className="w-12 h-12 flex items-center justify-center rounded-full glass-card transition-all duration-300"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 + (index * 0.1) }}
                                >
                                    <img src={social.icon} alt={social.alt} className="w-5 h-5 object-contain" />
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>

                    {/* Image/Avatar */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9, x: 50 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="flex-1 relative w-full flex justify-center mt-12 lg:mt-0"
                    >
                        <div className="relative w-72 h-72 sm:w-80 sm:h-80 lg:w-[450px] lg:h-[450px]">
                            {/* Decorative rings */}
                            <motion.div 
                                animate={{ rotate: 360 }}
                                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                                className="absolute inset-0 rounded-full border border-dashed border-accent-purple/40"
                            ></motion.div>
                            <motion.div 
                                animate={{ rotate: -360 }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                className="absolute inset-4 rounded-full border border-accent-cyan/30"
                            ></motion.div>
                            <motion.div 
                                animate={{ rotate: 360 }}
                                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                                className="absolute inset-8 rounded-full border border-dotted border-accent-blue/50"
                            ></motion.div>
                            
                            {/* Main Avatar */}
                            <div className="absolute inset-10 rounded-full overflow-hidden border-4 border-dark-800 shadow-[0_0_60px_rgba(59,130,246,0.3)] z-10 glass-card p-2 bg-dark-900/50">
                                <div className="w-full h-full rounded-full overflow-hidden">
                                    <img 
                                        src={perfil} 
                                        alt="Cesar Fuentes" 
                                        className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-700"
                                    />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Banner;